import { useCallback, useEffect, useState } from "react"
import { useAuth } from "react-oidc-context"
import {
  listProjects,
  createProject,
  getProject,
  updateProject,
  deleteProject,
  getKnowledgeFileUploadUrl,
  deleteKnowledgeFile,
  ProjectSummary,
  ProjectDetail,
  CreateProjectData,
  UpdateProjectData,
} from "@/services/projectService"
import { uploadFileToS3 } from "@/services/uploadService"

export function useProjects() {
  const auth = useAuth()
  const [projects, setProjects] = useState<ProjectSummary[]>([])
  const [currentProjectId, setCurrentProjectId] = useState<string | null>(null)
  const [currentProject, setCurrentProject] = useState<ProjectDetail | null>(null)
  const [isLoadingProjects, setIsLoadingProjects] = useState(false)

  const getIdToken = useCallback(() => {
    const token = auth.user?.id_token
    if (!token) throw new Error("Authentication required")
    return token
  }, [auth.user?.id_token])

  const loadProjects = useCallback(async () => {
    try {
      setIsLoadingProjects(true)
      const idToken = getIdToken()
      const data = await listProjects(idToken)
      data.sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())
      setProjects(data)
    } catch (err) {
      console.error("Failed to load projects:", err)
    } finally {
      setIsLoadingProjects(false)
    }
  }, [getIdToken])

  useEffect(() => {
    if (auth.isAuthenticated) {
      loadProjects()
    }
  }, [auth.isAuthenticated, loadProjects])

  const selectProject = useCallback(
    async (projectId: string | null) => {
      setCurrentProjectId(projectId)
      if (projectId && projectId !== "__unassigned__") {
        try {
          const idToken = getIdToken()
          const detail = await getProject(idToken, projectId)
          setCurrentProject(detail)
        } catch (err) {
          console.error("Failed to load project:", err)
          setCurrentProject(null)
        }
      } else {
        setCurrentProject(null)
      }
    },
    [getIdToken]
  )

  const handleCreateProject = useCallback(
    async (data: CreateProjectData): Promise<string> => {
      const idToken = getIdToken()
      const result = await createProject(idToken, data)
      await loadProjects()
      return result.projectId
    },
    [getIdToken, loadProjects]
  )

  const handleUpdateProject = useCallback(
    async (projectId: string, data: UpdateProjectData) => {
      const idToken = getIdToken()
      await updateProject(idToken, projectId, data)
      await loadProjects()
      if (projectId === currentProjectId) {
        const detail = await getProject(idToken, projectId)
        setCurrentProject(detail)
      }
    },
    [getIdToken, loadProjects, currentProjectId]
  )

  const handleDeleteProject = useCallback(
    async (projectId: string) => {
      const idToken = getIdToken()
      await deleteProject(idToken, projectId)
      if (projectId === currentProjectId) {
        setCurrentProjectId(null)
        setCurrentProject(null)
      }
      await loadProjects()
    },
    [getIdToken, loadProjects, currentProjectId]
  )

  const uploadKnowledgeFile = useCallback(
    async (projectId: string, file: File) => {
      const idToken = getIdToken()
      const { uploadUrl, fileKey } = await getKnowledgeFileUploadUrl(
        idToken,
        projectId,
        file.name,
        file.type
      )
      await uploadFileToS3(file, uploadUrl)
      // Refresh project to get updated knowledge files list
      const detail = await getProject(idToken, projectId)
      setCurrentProject(detail)
      await loadProjects()
      return fileKey
    },
    [getIdToken, loadProjects]
  )

  const removeKnowledgeFile = useCallback(
    async (projectId: string, fileKey: string) => {
      const idToken = getIdToken()
      await deleteKnowledgeFile(idToken, projectId, fileKey)
      const detail = await getProject(idToken, projectId)
      setCurrentProject(detail)
      await loadProjects()
    },
    [getIdToken, loadProjects]
  )

  return {
    projects,
    currentProjectId,
    currentProject,
    isLoadingProjects,
    loadProjects,
    selectProject,
    createProject: handleCreateProject,
    updateProject: handleUpdateProject,
    deleteProject: handleDeleteProject,
    uploadKnowledgeFile,
    removeKnowledgeFile,
  }
}
